import React, {
  createRef, useEffect, useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf, faDownload, faUpload, faFileContract,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { contractDocumentsV2Css } from '../../../screens/CompletedQuotes/CompletedV2.styles';
import {
  generalAlertError,
} from '../../utils/alerts';
import { sonnerErrorAlert, sonnerSuccessAlert } from '../../utils/sonnerAlerts';
import { documentMethodManualUpload } from '../../../services/axios-client/axeCommons';
import DocumentsToSendModal from './DocumentsToSendModal';
import managementAxe from '../../../services/axios-client/managementServisAxeClient';
import { GenderEnum } from '../../constants';

const ContractDocumentsStyles = styled.div`
  .contract-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: none;
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }
  }

  .card-header-custom {
    background: linear-gradient(135deg, #00a651 0%, #00873f 100%);
    padding: 1rem 1.25rem;
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;

    .header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .header-icon {
      width: 36px;
      height: 36px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 1rem;
    }

    .header-title {
      color: #ffffff;
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
    }

    .signature-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      a {
        color: #ffffff;
        font-size: 0.95rem;
        font-weight: 500;
        text-decoration: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.6);
        transition: border-color 0.2s ease;

        &:hover {
          border-color: #ffffff;
        }
      }

      .copy-btn {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        border-radius: 6px;
        padding: 0.4rem 0.5rem;
        color: #ffffff;
        cursor: pointer;
        transition: background 0.2s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }

    .signed-badge {
      background: rgba(255, 255, 255, 0.2);
      padding: 0.4rem 0.75rem;
      border-radius: 6px;
      color: #ffffff;
      font-size: 0.875rem;
      font-weight: 500;
    }
  }

  .documents-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    padding: 1.25rem;
  }

  .document-card {
    width: 300px;
    background: #ffffff;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      border-color: #00a651;
      box-shadow: 0 4px 12px rgba(0, 166, 81, 0.15);
    }
  }

  .document-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .document-type-label {
    background: #f3f4f6;
    color: #374151;
    padding: 0.35rem 0.6rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .status-badge {
    padding: 0.35rem 0.6rem;
    border-radius: 6px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;

    &.status-valid {
      background: #dcfce7;
      color: #166534;
    }

    &.status-invalid {
      background: #fee2e2;
      color: #991b1b;
    }

    &.status-pending {
      background: #fef3c7;
      color: #92400e;
    }

    &.status-not-required {
      background: #e5e7eb;
      color: #4b5563;
    }
  }

  .document-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-grow: 1;
    min-height: 80px;

    .pdf-icon {
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      background: #fef2f2;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #dc2626;
      font-size: 1.25rem;
    }

    .document-name {
      font-size: 0.875rem;
      font-weight: 500;
      color: #1f2937;
      word-break: break-word;
      line-height: 1.4;
    }
  }

  .document-actions {
    display: flex;
    gap: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid #f0f0f0;

    .btn-download {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      padding: 0.6rem 1rem;
      background: linear-gradient(135deg, #00a651 0%, #00873f 100%);
      border: none;
      border-radius: 8px;
      color: #ffffff;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 166, 81, 0.3);
      }
    }

    .btn-upload {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      padding: 0.6rem 1rem;
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      border: none;
      border-radius: 8px;
      color: #ffffff;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
      }
    }
  }

  .button-row {
    display: flex;
    justify-content: flex-end;
    padding: 1rem 1.25rem;
  }

  .action-button {
    background: #f59e0b;
    border: none;
    border-radius: 8px;
    padding: 0.5rem 1.2rem;
    color: #ffffff;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
      background: #d97706;
    }

    &:active {
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    .documents-grid {
      justify-content: center;
    }

    .document-card {
      width: 100%;
      max-width: 340px;
    }
  }

  ${(p) => p.$v2 && contractDocumentsV2Css}
`;

const schema = yup.object({
  email: yup.string().email('Inserisci un indirizzo email valido').required('L\'email è obbligatoria'),
});

function ContractDocumentsOffline({
  insuranceRequest,
  downloadDocument,
  quote,
  collectQuoteData,
  v2 = false,
}) {
  const [optionalRefs, setOptionalRefs] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [sendingDocuments, setSendingDocuments] = useState(false);
  const {
    register, formState: { errors }, watch, reset, trigger,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const formData = watch();

  const fireSignedDocUpload = (docId) => {
    optionalRefs[docId].current.click();
  };

  const sendDocumentsToClient = async (data) => {
    setSendingDocuments(true);
    try {
      const response = await managementAxe.post(`/broker/after-sale/quotation/${quote.id}/send-documents-to-client`, data);

      if (response.status !== 200) {
        sonnerErrorAlert('Errore durante l\'invio dei documenti. Riprova.');
        setSendingDocuments(false);
        return;
      }

      sonnerSuccessAlert('Documenti inviati con successo!');
      setShowModal(false);
      setSendingDocuments(false);
      reset();
    } catch (error) {
      sonnerErrorAlert('Errore durante l\'invio dei documenti. Riprova.');
      setSendingDocuments(false);
    }
  };

  const uploadSignedDocument = async (documentId, files) => {
    const uploadedFile = files[0];
    const reader = new FileReader();

    reader.readAsDataURL(uploadedFile);
    if (uploadedFile.type !== 'application/pdf') {
      generalAlertError('Formato di file errato. Il file deve essere in .pdf');
      return;
    }
    reader.onload = () => {
      documentMethodManualUpload(quote.id, documentId, {
        document: reader.result.replace('data:application/pdf;base64,', ''),
      }).then(() => {
        collectQuoteData();
        sonnerSuccessAlert('File caricato');
      });
    };
  };

  useEffect(() => {
    const docRefs = {};
    insuranceRequest.documents.filter((d) => d.signable_document === 1).forEach((doc) => {
      docRefs[doc.id] = createRef(null);
    });

    setOptionalRefs(docRefs);
  }, [JSON.stringify(insuranceRequest.documents.filter((d) => d.signable_document === 1))]);

  const getSignatureStatusClass = (document) => {
    if (document.signable_document !== 1) return 'status-not-required';
    if (document.is_signed_valid === 1) return 'status-valid';
    if (document.is_signed_valid === 0) return 'status-invalid';
    return 'status-pending';
  };

  const getSignatureStatusText = (document) => {
    if (document.signable_document !== 1) return 'Non Richiede Firma';
    if (document.is_signed_valid === 1 && document.signed_document_filename !== null) return 'Firmato';
    if (document.is_signed_valid === 0) return 'Firma Non Valida';
    if (document.is_signed_valid === null && document.signed_document_filename !== null) return 'In Verifica';
    return 'Da Firmare';
  };

  // check if there are couments that needs signature and are not signed yet
  const hasDocumentsToSign = insuranceRequest.documents.some(
    (document) => document.signable_document === 1
      && (document.is_signed_valid === 0
        || (document.is_signed_valid === null
          && document.signed_document_filename === null)),
  );

  const getSignatureLink = () => {
    let frontendUrl = 'http://localhost:3015';
    const baseUrl = window.location.origin;

    switch (baseUrl) {
      case 'https://intermediari.greenia.it':
        frontendUrl = 'https://greenia.it';
        break;
      case 'https://staging.greenia.it':
        frontendUrl = 'https://staging-frontend.greenia.it';
        break;
      default:
        frontendUrl = 'http://localhost:3015';
        break;
    }

    return `${frontendUrl}/documents-signature/${insuranceRequest.request_token}`;
  };

  const getDocumentUploadText = (document) => {
    if ([13, 35, 36, 37, 38, 39, 40, 41].includes(document.document_type.id)) {
      return 'Carica Polizza Firmata';
    }

    if (document.document_type_id === 32) {
      return 'Carica Quietanza Firmata';
    }

    if (document.document_type_id === 34) {
      return 'Carica Documento Firmato';
    }

    if (document.document_type_id === 1005) {
      return 'Carica Documento Firmato';
    }

    return 'Carica Documento Firmato';
  };

  return (
    <ContractDocumentsStyles $v2={v2}>
      <div className="contract-card polizze-emessa-status-step-four">
        <div className="card-header-custom">
          <div className="header-left">
            <div className="header-icon">
              <FontAwesomeIcon icon={faFileContract} />
            </div>
            <h2 className="header-title">Documenti Polizza</h2>
          </div>
          {quote.quote_type === 'res_civile' && (
            <>
              {insuranceRequest.customer.gender !== GenderEnum.LegalPerson && quote.product_id != '10' && (
                hasDocumentsToSign ? (
                  <div className="signature-link">
                    <button className="action-button" onClick={() => setShowModal(true)}>
                      Invia Documenti
                    </button>
                    <a
                      href={getSignatureLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Firma i documenti
                    </a>
                    <button
                      type="button"
                      className="copy-btn"
                      title="Copia link"
                      onClick={() => {
                        navigator.clipboard.writeText(getSignatureLink());
                        sonnerSuccessAlert('Link copiato!');
                      }}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                ) : (
                  <span className="signed-badge">Documenti Già Firmati</span>
                )
              )}
            </>
          )}
        </div>

        <div className="documents-grid">
          {insuranceRequest.documents
            .filter((dr) => dr.signable_document === 1)
            .map((document, index) => (
              <div className="document-card" key={index}>
                <div className="document-header">
                  <span className="document-type-label" title={document.document_type.name}>
                    {document.document_type.name}
                  </span>
                  <span className={`status-badge ${getSignatureStatusClass(document)}`}>
                    {getSignatureStatusText(document)}
                  </span>
                </div>

                <div className="document-content">
                  <div className="pdf-icon">
                    <FontAwesomeIcon icon={faFilePdf} />
                  </div>
                  <span className="document-name">{document.original_filename}</span>
                </div>

                <div className="document-actions">
                  <button
                    className="btn-download w-100"
                    onClick={() => downloadDocument(document.id)}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    Scarica
                  </button>

                  {document.signable_document === 1
                    && (document.is_signed_valid === 0
                      || (document.is_signed_valid === null
                        && document.signed_document_filename === null)) && (
                  // <ActionsWrapperResultPage>
                  //   {[13, 35, 36, 37, 38, 39, 40, 41].includes(
                  //     document.document_type.id,
                  //   ) && (
                  //   <button
                  //     className="btn-upload"
                  //     onClick={() => fireSignedDocUpload(document.document_type_id)}
                  //   >
                  //     <FontAwesomeIcon icon={faUpload} />
                  //     {getDocumentUploadText(document)}
                  //     <input
                  //       ref={uploadPolizzaEmessa}
                  //       type="file"
                  //       className="d-none"
                  //       onChange={(e) => uploadSignedDocument(document.id, e.target.files)}
                  //     />
                  //   </button>
                  //   )}
                  //   {document.document_type.id === 34 && (
                  //   <button
                  //     className="btn-upload"
                  //     onClick={
                  //       document.signed_document_filename === null
                  //         ? () => fireSignedDocUpload(document.document_type.id)
                  //         : () => { }
                  //     }
                  //   >
                  //     <FontAwesomeIcon icon={faUpload} />
                  //     {getDocumentUploadText(document)}
                  //     <input
                  //       ref={uploadMergedDocFileRef}
                  //       type="file"
                  //       className="d-none"
                  //       onChange={(e) => uploadSignedDocument(document.id, e.target.files)}
                  //     />
                  //   </button>
                  //   )}
                  //   {![13, 34, 35, 36, 37, 38, 39, 40, 41].includes(
                  //     document.document_type.id,
                  //   ) && (
                  //   <button
                  //     className="btn-upload"
                  //     onClick={
                  //         document.signed_document_filename === null
                  //           ? () => fireSignedDocUpload(document.document_type.id, document.id)
                  //           : () => { }
                  //       }
                  //   >
                  //     <FontAwesomeIcon icon={faUpload} />
                  //     {getDocumentUploadText(document)}
                  //     <input
                  //       ref={optionalRefs[document.id]}
                  //       type="file"
                  //       className="d-none"
                  //       onChange={(e) => uploadSignedDocument(document.id, e.target.files)}
                  //     />
                  //   </button>
                  //   )}
                  // </ActionsWrapperResultPage>
                        <>
                          {[13, 35, 36, 37, 38, 39, 40, 41].includes(
                            document.document_type.id,
                          ) && (
                          <button
                            className="btn-upload w-100"
                            onClick={() => fireSignedDocUpload(document.id)}
                          >
                            <FontAwesomeIcon icon={faUpload} />
                            {getDocumentUploadText(document)}
                            <input
                              ref={optionalRefs[document.id]}
                              type="file"
                              className="d-none"
                              onChange={(e) => uploadSignedDocument(document.id, e.target.files)}
                            />
                          </button>
                          )}
                          {document.document_type.id === 34 && (
                          <button
                            className="btn-upload w-100"
                            onClick={
                              document.signed_document_filename === null
                                ? () => fireSignedDocUpload(document.id)
                                : () => { }
                            }
                          >
                            <FontAwesomeIcon icon={faUpload} />
                            {getDocumentUploadText(document)}
                            <input
                              ref={optionalRefs[document.id]}
                              type="file"
                              className="d-none"
                              onChange={(e) => uploadSignedDocument(document.id, e.target.files)}
                            />
                          </button>
                          )}
                          {![13, 34, 35, 36, 37, 38, 39, 40, 41].includes(
                            document.document_type.id,
                          ) && (
                          <button
                            className="btn-upload w-100"
                            onClick={
                                document.signed_document_filename === null
                                  ? () => fireSignedDocUpload(document.id)
                                  : () => { }
                              }
                          >
                            <FontAwesomeIcon icon={faUpload} />
                            {getDocumentUploadText(document)}
                            <input
                              ref={optionalRefs[document.id]}
                              type="file"
                              className="d-none"
                              onChange={(e) => uploadSignedDocument(document.id, e.target.files)}
                            />
                          </button>
                          )}
                        </>
                  )}
                </div>
              </div>
            ))}

          {quote.ancillary_quotation_documents && quote.ancillary_quotation_documents.map((document, index) => (
            <div className="document-card" key={`ancillary-${index}`}>
              <div className="document-header">
                <span className="document-type-label" title={document.document_type?.name}>
                  {document.document_type?.name}
                </span>
                <span className={`status-badge ${getSignatureStatusClass(document)}`}>
                  {getSignatureStatusText(document)}
                </span>
              </div>

              <div className="document-content">
                <div className="pdf-icon">
                  <FontAwesomeIcon icon={faFilePdf} />
                </div>
                <span className="document-name">{document.original_filename}</span>
              </div>

              <div className="document-actions">
                <button
                  className="btn-download w-100"
                  onClick={() => downloadDocument(document.id)}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Scarica
                </button>
              </div>
            </div>
          ))}
        </div>
        <DocumentsToSendModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            reset();
          }}
          register={register}
          errors={errors}
          formData={formData}
          sendDocumentsToClient={sendDocumentsToClient}
          sendingDocuments={sendingDocuments}
          trigger={trigger}
        />
      </div>
    </ContractDocumentsStyles>
  );
}

export default ContractDocumentsOffline;
